import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import Seo, { breadcrumbLd, getSiteUrl } from "@/components/Seo";

const SITE_URL = getSiteUrl();
const CERTIFICATE_IMAGE = "/assets/premio-sabor-granada-2023.png";
const CERTIFICATE_WIDTH = 1459;
const CERTIFICATE_HEIGHT = 1078;

/**
 * Verifiable award page for the "Sabor Granada 2023" recognition.
 *
 * - Bilingual (ES: /premio-sabor-granada-2023, EN: /en/sabor-granada-award-2023)
 * - Reciprocal hreflang + self-referential canonical
 * - BreadcrumbList JSON-LD only — no Review / AggregateRating / Award schema
 * - No external links (the Provincial Council's page does not name the winner)
 * - Reuses the certificate image already hosted for the site
 */
export default function AwardPage({ forceLang }) {
  const { lang, setLang } = useLang();
  useEffect(() => {
    if (forceLang && forceLang !== lang) setLang(forceLang);
  }, [forceLang, lang, setLang]);

  const effectiveLang = forceLang || lang;
  const isEs = effectiveLang === "es";

  const paths = {
    es: "/premio-sabor-granada-2023",
    en: "/en/sabor-granada-award-2023",
  };
  const canonical = `${SITE_URL}${isEs ? paths.es : paths.en}`;
  const alternates = {
    es: `${SITE_URL}${paths.es}`,
    en: `${SITE_URL}${paths.en}`,
    "x-default": `${SITE_URL}${paths.es}`,
  };

  const copy = isEs
    ? {
        seo_title:
          "Premio Sabor Granada 2023 · Mejor AOVE Ecológico · Cortijo de Santa María",
        seo_description:
          "Cortijo de Santa María, Mejor Aceite de Oliva Virgen Extra de Producción Ecológica en la IX edición de los Premios AOVE «Sabor Granada» (2023), concedidos por la Diputación de Granada.",
        overline: "Reconocimiento verificado",
        title: "Premio Sabor Granada 2023",
        subtitle:
          "Mejor Aceite de Oliva Virgen Extra de Producción Ecológica",
        intro:
          "Cortijo de Santa María recibió el reconocimiento a Mejor Aceite de Oliva Virgen Extra de Producción Ecológica en la IX edición de los Premios AOVE «Sabor Granada» (2023), concedidos por la Diputación de Granada.",
        facts_title: "Datos del reconocimiento",
        facts: [
          { k: "Categoría", v: "Mejor Aceite de Oliva Virgen Extra de Producción Ecológica" },
          { k: "Convocatoria", v: "IX edición de los Premios AOVE «Sabor Granada»" },
          { k: "Año", v: "2023" },
          { k: "Entidad concedente", v: "Diputación de Granada" },
          { k: "Galardonado", v: "Cortijo de Santa María · Limones, Moclín (Granada)" },
        ],
        certificate_title: "Certificado del reconocimiento",
        certificate_alt:
          "Certificado del premio Sabor Granada 2023 concedido a Cortijo de Santa María como mejor AOVE de producción ecológica",
        certificate_caption:
          "Certificado facilitado por la organización de los Premios AOVE «Sabor Granada».",
        breadcrumb_home: "Inicio",
        breadcrumb_current: "Premio Sabor Granada 2023",
        cta_title: "Prueba el aceite reconocido",
        cta_body:
          "La misma cosecha, embotellada en 500 ml de vidrio oscurecido protector.",
        cta_product: "Ver la botella 500 ml",
        cta_shop: "Ir a la tienda",
        product_href: "/producto/botella-500ml",
        shop_href: "/tienda",
      }
    : {
        seo_title:
          "Sabor Granada Award 2023 · Best Organic EVOO · Cortijo de Santa María",
        seo_description:
          "Cortijo de Santa María received the Best Organic Extra Virgin Olive Oil award at the 9th «Sabor Granada» EVOO Awards (2023), presented by the Provincial Council of Granada.",
        overline: "Verified recognition",
        title: "Sabor Granada Award 2023",
        subtitle: "Best Organic Extra Virgin Olive Oil",
        intro:
          "Cortijo de Santa María received the Best Organic Extra Virgin Olive Oil award at the 9th «Sabor Granada» EVOO Awards (2023), presented by the Provincial Council of Granada.",
        facts_title: "Award details",
        facts: [
          { k: "Category", v: "Best Organic Extra Virgin Olive Oil" },
          { k: "Edition", v: "9th «Sabor Granada» EVOO Awards" },
          { k: "Year", v: "2023" },
          { k: "Presented by", v: "Provincial Council of Granada" },
          { k: "Recipient", v: "Cortijo de Santa María · Limones, Moclín (Granada, Spain)" },
        ],
        certificate_title: "Award certificate",
        certificate_alt:
          "Sabor Granada 2023 award certificate presented to Cortijo de Santa María as best organic EVOO",
        certificate_caption:
          "Certificate provided by the organisers of the «Sabor Granada» EVOO Awards.",
        breadcrumb_home: "Home",
        breadcrumb_current: "Sabor Granada Award 2023",
        cta_title: "Taste the awarded oil",
        cta_body:
          "The same early-harvest oil, bottled in 500 ml protective dark glass.",
        cta_product: "See the 500 ml bottle",
        cta_shop: "Go to the shop",
        product_href: "/product/botella-500ml",
        shop_href: "/shop",
      };

  const breadcrumb = breadcrumbLd([
    { name: copy.breadcrumb_home, url: `${SITE_URL}${isEs ? "/" : "/en"}` },
    { name: copy.breadcrumb_current, url: canonical },
  ]);

  return (
    <main
      data-testid="award-page"
      className="min-h-screen bg-cream-100 text-charcoal"
    >
      <Seo
        title={copy.seo_title}
        description={copy.seo_description}
        canonical={canonical}
        alternates={alternates}
        lang={effectiveLang}
        ogImage={CERTIFICATE_IMAGE}
        jsonLd={[breadcrumb]}
      />
      <ShopNav />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-14 sm:pt-20 pb-6">
        <nav
          aria-label="Breadcrumb"
          data-testid="award-breadcrumb"
          className="text-[11px] uppercase tracking-[0.28em] text-olive-800/70"
        >
          <Link to={isEs ? "/" : "/en"} className="hover:text-gold-deep">
            {copy.breadcrumb_home}
          </Link>
          <span className="mx-3 text-olive-800/40">/</span>
          <span className="text-olive-900">{copy.breadcrumb_current}</span>
        </nav>

        <div className="mt-10 max-w-3xl">
          <span
            data-testid="award-overline"
            className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium"
          >
            {copy.overline}
          </span>
          <h1
            data-testid="award-title"
            className="mt-6 font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] text-olive-900"
          >
            {copy.title}
          </h1>
          <p
            data-testid="award-subtitle"
            className="mt-4 font-serif italic text-xl sm:text-2xl text-olive-800"
          >
            {copy.subtitle}
          </p>
          <p
            data-testid="award-intro"
            className="mt-6 text-olive-800/85 font-light text-lg leading-relaxed"
          >
            {copy.intro}
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 sm:py-14">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-12 lg:gap-20 items-start">
          <figure
            data-testid="award-certificate"
            className="bg-olive-900 border border-olive-800/60 rounded-sm p-8 sm:p-12"
          >
            <img
              src={CERTIFICATE_IMAGE}
              alt={copy.certificate_alt}
              width={CERTIFICATE_WIDTH}
              height={CERTIFICATE_HEIGHT}
              loading="lazy"
              decoding="async"
              data-testid="award-certificate-image"
              className="w-full h-auto object-contain drop-shadow-[0_18px_44px_rgba(0,0,0,0.35)]"
            />
            <figcaption
              data-testid="award-certificate-caption"
              className="mt-6 text-[12px] uppercase tracking-[0.28em] text-cream-100/70 text-center"
            >
              {copy.certificate_caption}
            </figcaption>
          </figure>

          <div>
            <h2
              data-testid="award-facts-title"
              className="font-serif text-2xl sm:text-3xl text-olive-900"
            >
              {copy.facts_title}
            </h2>
            <dl
              data-testid="award-facts"
              className="mt-8 border-t border-olive-700/15"
            >
              {copy.facts.map(({ k, v }) => (
                <div
                  key={k}
                  className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 sm:gap-8 py-5 border-b border-olive-700/15"
                >
                  <dt className="text-[11px] uppercase tracking-[0.28em] text-olive-800/70">
                    {k}
                  </dt>
                  <dd className="font-serif text-lg text-olive-900">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="bg-olive-900 text-cream-100 border-t border-olive-800/60">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 sm:py-20 text-center">
          <h2
            data-testid="award-cta-title"
            className="font-serif text-3xl sm:text-4xl leading-[1.1]"
          >
            {copy.cta_title}
          </h2>
          <p
            data-testid="award-cta-body"
            className="mt-4 max-w-2xl mx-auto text-cream-100/80 font-light text-lg"
          >
            {copy.cta_body}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={copy.product_href}
              data-testid="award-cta-product"
              className="inline-flex items-center justify-center bg-gold text-olive-900 hover:bg-cream-100 transition-colors text-[13px] uppercase tracking-[0.28em] px-8 py-4"
            >
              {copy.cta_product}
            </Link>
            <Link
              to={copy.shop_href}
              data-testid="award-cta-shop"
              className="inline-flex items-center justify-center border border-cream-100/40 hover:border-cream-100 text-cream-100 text-[13px] uppercase tracking-[0.28em] px-8 py-4"
            >
              {copy.cta_shop}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
